document.documentElement.classList.remove('no-js');

document.addEventListener('DOMContentLoaded', () => {
  const getMenu = () => document.querySelector('[data-menu]');
  const getCart = () => document.querySelector('[data-cart-drawer]');
  const backdrop = document.querySelector('[data-drawer-backdrop]');
  let lockedScrollY = 0;
  let isScrollLocked = false;
  let cartMutationPending = false;

  // Keep every fixed drawer outside the sticky, blurred header. Besides
  // becoming a containing block for fixed children, the header sits below the
  // backdrop in the stacking order and would leave the menu visibly dimmed.
  const mountDrawerLayers = () => {
    const menuDrawer = getMenu();
    const cartDrawer = getCart();
    if (menuDrawer && menuDrawer.parentElement !== document.body) document.body.appendChild(menuDrawer);
    if (cartDrawer && cartDrawer.parentElement !== document.body) document.body.appendChild(cartDrawer);
    if (backdrop && backdrop.parentElement !== document.body) document.body.appendChild(backdrop);
  };

  mountDrawerLayers();

  const header = document.querySelector('.mimo-header');
  let previousPageY = Math.max(0, window.scrollY);
  let headerFrame;

  const updateHeaderVisibility = () => {
    if (!header) return;
    const currentPageY = Math.max(0, window.scrollY);
    const isAtTop = currentPageY <= 4;
    const delta = currentPageY - previousPageY;
    header.classList.toggle('is-scrolled', !isAtTop);

    if (isAtTop || isScrollLocked) {
      header.classList.remove('is-hidden');
    } else if (Math.abs(delta) >= 3) {
      header.classList.toggle('is-hidden', delta > 0);
    }

    previousPageY = currentPageY;
  };

  window.addEventListener('scroll', () => {
    cancelAnimationFrame(headerFrame);
    headerFrame = requestAnimationFrame(updateHeaderVisibility);
  }, { passive: true });
  updateHeaderVisibility();

  const lockPageScroll = () => {
    if (isScrollLocked) return;
    lockedScrollY = window.scrollY;
    isScrollLocked = true;
    document.documentElement.classList.add('drawer-open');
    document.body.classList.add('no-scroll');
    document.body.style.top = `-${lockedScrollY}px`;
  };

  const unlockPageScroll = () => {
    if (!isScrollLocked) return;
    isScrollLocked = false;
    document.documentElement.classList.remove('drawer-open');
    document.body.classList.remove('no-scroll');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  };

  const closeDrawers = () => {
    getMenu()?.classList.remove('is-open');
    getMenu()?.setAttribute('aria-hidden', 'true');
    getCart()?.classList.remove('is-open');
    getCart()?.setAttribute('aria-hidden', 'true');
    document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
    document.querySelector('[data-cart-toggle]')?.setAttribute('aria-expanded', 'false');
    backdrop?.classList.remove('is-open');
    unlockPageScroll();
  };

  const openDrawer = (kind) => {
    closeDrawers();
    const drawer = kind === 'menu' ? getMenu() : getCart();
    const toggle = document.querySelector(kind === 'menu' ? '[data-menu-toggle]' : '[data-cart-toggle]');
    if (drawer) void drawer.offsetWidth;
    drawer?.classList.add('is-open');
    drawer?.setAttribute('aria-hidden', 'false');
    toggle?.setAttribute('aria-expanded', 'true');
    backdrop?.classList.add('is-open');
    lockPageScroll();
    drawer?.querySelector('[data-drawer-close]')?.focus();
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cart-toggle]')) return;
    event.preventDefault();
    openDrawer('cart');
  }, { capture: true });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-menu-toggle]')) return openDrawer('menu');
    if (event.target.closest('[data-drawer-close], [data-drawer-backdrop]')) closeDrawers();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawers();
  });

  const refreshCartDrawer = async () => {
    const currentDrawer = getCart();
    const wasOpen = currentDrawer?.classList.contains('is-open') || false;
    const previousScrollTop = currentDrawer?.querySelector('[data-cart-drawer-body]')?.scrollTop || 0;
    const refreshUrl = new URL(window.location.href);
    refreshUrl.searchParams.set('_mimo_cart_refresh', Date.now().toString());
    const response = await fetch(refreshUrl.toString(), {
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Não foi possível atualizar o carrinho.');
    const html = await response.text();
    const nextDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = nextDocument.querySelector('[data-cart-drawer]');
    if (nextDrawer && currentDrawer) {
      if (wasOpen) {
        nextDrawer.classList.add('is-open');
        nextDrawer.setAttribute('aria-hidden', 'false');
      }
      currentDrawer.replaceWith(nextDrawer);
      if (nextDrawer.parentElement !== document.body) document.body.appendChild(nextDrawer);
      const nextBody = nextDrawer.querySelector('[data-cart-drawer-body]');
      if (nextBody) nextBody.scrollTop = previousScrollTop;
    }
    const nextCount = nextDocument.querySelector('[data-cart-count]');
    const currentCount = document.querySelector('[data-cart-count]');
    if (nextCount && currentCount) currentCount.replaceWith(nextCount);
    document.dispatchEvent(new CustomEvent('mimo:cart:updated'));
    return getCart();
  };

  document.addEventListener('mimo:cart:refresh-open', async () => {
    await refreshCartDrawer();
    openDrawer('cart');
  });

  document.addEventListener('mimo:cart:refresh', async () => {
    await refreshCartDrawer();
  });

  document.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-remove], [data-cart-decrease], [data-cart-increase]');
    if (!control) return;
    event.preventDefault();
    if (cartMutationPending) return;

    const line = control.closest('[data-cart-line]');
    const lineKey = line?.dataset.lineKey;
    const currentQuantity = Number(line?.dataset.lineQuantity || 0);
    if (!lineKey || currentQuantity < 1) return;

    let nextQuantity = currentQuantity;
    if (control.matches('[data-cart-remove]')) nextQuantity = 0;
    if (control.matches('[data-cart-decrease]')) nextQuantity = Math.max(0, currentQuantity - 1);
    if (control.matches('[data-cart-increase]')) nextQuantity = currentQuantity + 1;

    cartMutationPending = true;
    const drawer = getCart();
    drawer?.classList.add('is-updating');
    drawer?.querySelectorAll('button').forEach((button) => { button.disabled = true; });
    const error = drawer?.querySelector('[data-cart-error]');
    if (error) error.textContent = '';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: lineKey, quantity: nextQuantity })
      });
      if (!response.ok) throw new Error('Não foi possível atualizar o artigo.');
      const refreshedDrawer = await refreshCartDrawer();
      refreshedDrawer?.classList.remove('is-updating');
    } catch (requestError) {
      const activeDrawer = getCart();
      activeDrawer?.classList.remove('is-updating');
      activeDrawer?.querySelectorAll('button').forEach((button) => { button.disabled = false; });
      const activeError = activeDrawer?.querySelector('[data-cart-error]');
      if (activeError) activeError.textContent = 'Não foi possível atualizar o carrinho. Tenta novamente.';
    } finally {
      cartMutationPending = false;
    }
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const originalLabel = button?.textContent;
      if (button) { button.disabled = true; button.textContent = 'A adicionar…'; }
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Não foi possível adicionar o artigo.');
        await refreshCartDrawer();
        if (button) button.textContent = 'Adicionado ✓';
        await new Promise((resolve) => window.setTimeout(resolve, 700));
      } catch (error) {
        form.submit();
      } finally {
        if (button) { button.disabled = false; button.textContent = originalLabel; }
      }
    });
  });
});
